import { toast } from "sonner";
import {
  modelRegistryApi,
  registryErrorMessage,
} from "@/services/modelRegistryApi";
import type { AdminModel, ModelRoleBinding } from "@/types/modelRegistry";
import { ROLE_LABELS } from "./format";
import { Panel, SelectInput, Skeleton } from "./ui";

interface RolesBarProps {
  roles: ModelRoleBinding[] | null;
  models: AdminModel[] | null;
  onChanged: () => Promise<void>;
}

function RoleRow({
  binding,
  options,
  onChanged,
}: {
  binding: ModelRoleBinding;
  options: AdminModel[];
  onChanged: () => Promise<void>;
}) {
  const meta = ROLE_LABELS[binding.role];
  const current = options.find((m) => m.id === binding.modelId);
  const change = async (modelId: string) => {
    if (!modelId || modelId === binding.modelId) return;
    try {
      await modelRegistryApi.setRole(binding.role, modelId);
      toast.success(
        `${meta.label} now uses ${options.find((m) => m.id === modelId)?.name ?? "that model"}`,
      );
      await onChanged();
    } catch (error) {
      toast.error(registryErrorMessage(error));
    }
  };
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-medium text-gray-900">{meta.label}</span>
        <span className="truncate text-xs text-gray-500">{meta.hint}</span>
      </div>
      <div className="flex w-72 shrink-0 flex-col items-end gap-1">
        <SelectInput
          aria-label={`${meta.label} model`}
          value={binding.modelId ?? ""}
          onChange={(e) => void change(e.target.value)}
          options={[
            { value: "", label: "Choose a model" },
            ...options.map((m) => ({ value: m.id, label: m.name })),
          ]}
        />
        {!binding.modelId && (
          <span className="text-xs text-amber-600">
            Not set. Chats on Auto will fail.
          </span>
        )}
        {current && (
          <span className="font-mono text-[11px] text-gray-400">
            {current.modelName}
          </span>
        )}
      </div>
    </div>
  );
}

/** One row per role. Grows down, never sideways, so any number of roles fits. */
export function RolesBar({ roles, models, onChanged }: RolesBarProps) {
  if (!roles || !models) return <Skeleton className="h-40" />;
  const options = models.filter(
    (m) => m.kind === "chat" && m.state !== "disabled",
  );
  return (
    <Panel className="divide-y divide-gray-100">
      {roles.map((binding) => (
        <RoleRow
          key={binding.role}
          binding={binding}
          options={options}
          onChanged={onChanged}
        />
      ))}
    </Panel>
  );
}
